import {
	IconAdjustments,
	IconAlignJustified,
	IconBrandLoom,
	IconBrandVimeo,
	IconBrandYoutube,
	IconForms,
	IconHeadphones,
	IconListCheck,
	IconVideo,
} from "@tabler/icons-react"
import { type ComponentType, type FC } from "react"

import {
	type BokaderoCourse,
	type BokaderoCoursePage,
	type BokaderoIntendedPageType,
} from "@forento/core/models/bokadero"
import { exhaustiveGuard } from "@forento/core/utilities/switch"
import LexicalViewer from "@forento/ui/components/LexicalViewer"
import { BunnyPlayer, LoomPlayer, VimeoPlayer, YoutubePlayer } from "@forento/ui/components/VideoPlayer"

import CourseStatus from "~/components/course-status"

type Props = { course: Extract<BokaderoCourse, { status: "active" | "submitted" | "rejected" }> }
const ViewContent: FC<Props> = ({ course }) => (
	<div className="mx-auto w-full max-w-4xl space-y-6">
		<div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 shadow-sm lg:flex-row lg:items-start lg:justify-between">
			<div className="flex grow flex-col">
				<h1 className="mb-2 text-2xl font-bold text-slate-900">{course.name}</h1>
				<CourseStatus status={course.status} className="mb-4" />
				<div>
					<h2 className="font-semibold text-slate-800">📝 Kort beskrivning</h2>
					<p className="leading-relaxed text-slate-700">{course.shortDescription}</p>
				</div>
			</div>

			{course.thumbnailUrl && (
				<img
					src={course.thumbnailUrl}
					alt="Kursbild"
					className="aspect-video w-64 rounded-lg border-2 border-slate-200 object-cover shadow-sm"
				/>
			)}
		</div>

		{course.presentationHtml && (
			<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
				<h2 className="mb-4 text-lg font-semibold text-slate-800">Presentation</h2>
				<div className="rich-content" dangerouslySetInnerHTML={{ __html: course.presentationHtml }} />
			</div>
		)}

		<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
			<h2 className="mb-6 text-lg font-semibold text-slate-800">📚 Kapitel & sidor</h2>

			{course.chapters.length === 0 ? (
				<div className="py-12 text-center">
					<div className="mb-4 text-6xl">📚</div>
					<p className="mb-2 text-lg text-slate-500">Inga kapitel har lagts till ännu</p>
				</div>
			) : (
				<div className="space-y-4">
					{course.chapters.map((chapter, index) => (
						<div
							key={chapter.id}
							className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 transition-shadow hover:shadow-md"
						>
							<div className="mb-3 flex items-center justify-between gap-4">
								<div className="flex flex-1 items-center">
									<span className="bg-primary mr-4 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white">
										{index + 1}
									</span>
									<h3 className="mr-2 truncate text-base font-semibold text-slate-900">
										{chapter.name}
									</h3>
								</div>
							</div>
							{chapter.pages.length === 0 ? (
								<div className="ml-10 flex items-center gap-2 text-sm text-slate-500">
									<span></span>
									Inga sidor i detta kapitel
								</div>
							) : (
								<div className="space-y-2">
									{chapter.pages.map((page, pageIndex) => (
										<Page key={page.id} page={page} pageIndex={pageIndex} />
									))}
								</div>
							)}
						</div>
					))}
				</div>
			)}
		</div>

		<div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
			{course.status === "active" ? (
				<div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
					<span className="text-2xl">✅</span>
					<div>
						<div className="font-semibold text-green-800">Kursen är aktiv</div>
						<div className="text-sm text-green-600">Kursen är synlig för användare och kan användas</div>
					</div>
				</div>
			) : course.status === "submitted" ? (
				<div className="flex items-center gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
					<span className="text-2xl">⏳</span>
					<div>
						<div className="font-semibold text-yellow-800">Kursen är inskickad</div>
						<div className="text-sm text-yellow-600">Väntar på granskning av administratör</div>
					</div>
				</div>
			) : course.status === "rejected" ? (
				<div className="flex items-center gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
					<span className="text-2xl">⏳</span>
					<div>
						<div className="font-semibold text-yellow-800">Kursen är inte godkänd</div>
						<div className="text-sm text-yellow-600">{course.rejectReason}</div>
					</div>
				</div>
			) : (
				exhaustiveGuard(course)
			)}
		</div>
	</div>
)

const pageTypeIcons: Record<BokaderoIntendedPageType, ComponentType<{ className?: string }>> = {
	video: IconVideo,
	youtube: IconBrandYoutube,
	vimeo: IconBrandVimeo,
	audio: IconHeadphones,
	quiz: IconListCheck,
	input: IconForms,
	text: IconAlignJustified,
	loom: IconBrandLoom,
	custom: IconAdjustments,
}
const pageTypeLabels: Record<BokaderoIntendedPageType, string> = {
	text: "Text",
	video: "Video",
	youtube: "YouTube",
	vimeo: "Vimeo",
	loom: "Loom",
	audio: "Ljud",
	quiz: "Quiz",
	input: "Input",
	custom: "Custom",
}

const Page: FC<{ page: BokaderoCoursePage; pageIndex: number }> = ({ page, pageIndex }) => {
	const Icon = pageTypeIcons[page.intendedType]

	return (
		<div className="rounded-lg border border-gray-200 bg-white p-4">
			<div className="mb-3 flex items-center gap-3">
				<span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
					{pageIndex + 1}
				</span>
				<div className="flex items-center gap-2">
					<Icon className="size-4 text-gray-500" />
					<span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
						{pageTypeLabels[page.intendedType]}
					</span>
				</div>
				<h4 className="text-base font-semibold text-gray-900">{page.name}</h4>
			</div>

			{page.imageUrl && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Bild</h5>
					<img src={page.imageUrl} alt="Bild" className="aspect-video w-full max-w-sm object-cover" />
				</div>
			)}

			{page.contentHtml && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Innehåll</h5>
					{page.intendedType === "custom" ? (
						<div dangerouslySetInnerHTML={{ __html: page.contentHtml }} className="rich-content" />
					) : (
						<LexicalViewer html={page.contentHtml} />
					)}
				</div>
			)}

			{page.audioUrl && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Ljud</h5>
					<audio controls src={page.audioUrl} />
				</div>
			)}

			{(page.bunnyVideoId || page.youTubeVideoId || page.vimeoVideoId || page.loomVideoId) && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Video</h5>
					<div className="max-w-sm">
						{page.bunnyVideoId ? (
							<BunnyPlayer videoId={page.bunnyVideoId} />
						) : page.youTubeVideoId ? (
							<YoutubePlayer videoId={page.youTubeVideoId} />
						) : page.vimeoVideoId ? (
							<VimeoPlayer videoId={page.vimeoVideoId} />
						) : page.loomVideoId ? (
							<LoomPlayer videoId={page.loomVideoId} />
						) : null}
					</div>
				</div>
			)}

			{page.intendedType === "input" && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Inmatning</h5>
					<div className="text-sm text-gray-600">
						{page.inputType === "single-line" ? "En rad" : "Flera rader"}
					</div>
				</div>
			)}

			{page.answers.length > 0 && (
				<div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4">
					<div className="mb-3 flex items-center gap-2">
						<span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-100 text-lg font-bold text-green-600 shadow">
							📝
						</span>
						<h5 className="text-base font-semibold text-green-800">Quiz</h5>
					</div>
					<ul className="space-y-2">
						{page.answers.map(answer => (
							<li
								key={answer.id}
								className={`flex items-center gap-3 rounded-md px-3 py-2 ${
									answer.isCorrect
										? "border border-green-200 bg-green-100"
										: "border border-gray-200 bg-white"
								}`}
							>
								<span
									className={`flex h-5 w-5 items-center justify-center rounded-full border ${
										answer.isCorrect
											? "border-green-500 bg-green-500 text-white"
											: "border-gray-300 bg-gray-100 text-gray-400"
									} text-xs font-bold`}
								>
									{answer.isCorrect ? "✓" : "○"}
								</span>
								<span
									className={`text-sm ${
										answer.isCorrect ? "font-semibold text-green-700" : "text-gray-700"
									}`}
								>
									{answer.text}
								</span>
							</li>
						))}
					</ul>
				</div>
			)}

			{page.attachments.length > 0 && (
				<div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Bilagor</h5>
					<div className="space-y-1">
						{page.attachments.map(attachment => (
							<div key={attachment.id} className="text-sm text-gray-600">
								{attachment.label}
							</div>
						))}
					</div>
				</div>
			)}

			{page.links.length > 0 && (
				<div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h5 className="mb-2 text-sm font-semibold text-gray-700">Länkar</h5>
					<div className="space-y-1">
						{page.links.map(link => (
							<div key={link.id} className="text-sm text-gray-600">
								<a href={link.url} target="_blank" rel="noopener noreferrer">
									<span className="font-medium">{link.label}</span> - {link.url}
								</a>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	)
}

export default ViewContent
